import React from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import type { FormInstance } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { CreateSupplierData, UpdateSupplierData, Supplier } from '../../services/supplierService';

interface SupplierFormProps {
  supplier?: Supplier;
  onSave: (data: CreateSupplierData | UpdateSupplierData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  noCard?: boolean;
  form?: FormInstance;
  hideActions?: boolean;
}

const SupplierForm: React.FC<SupplierFormProps> = ({
  supplier,
  onSave,
  onCancel,
  loading = false,
  noCard = false,
  form: externalForm,
  hideActions = false,
}) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const isEdit = !!supplier;

  React.useEffect(() => {
    if (supplier) {
      form.setFieldsValue({
        name: supplier.name,
        code: supplier.code,
        address: supplier.address,
        phone: supplier.phone,
        email: supplier.email,
        contact_person: supplier.contact_person,
      });
    }
  }, [supplier, form]);

  const handleSubmit = async (values: any) => {
    try {
      await onSave(values);
      form.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Có lỗi xảy ra!');
    }
  };

  const formContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        name: '',
        code: '',
        address: '',
        phone: '',
        email: '',
        contact_person: '',
      }}
    >
      <Form.Item
        name="name"
        label="Tên nhà cung cấp"
        rules={[{ required: true, message: 'Vui lòng nhập tên nhà cung cấp!' }]}
      >
        <Input placeholder="Nhập tên nhà cung cấp" />
      </Form.Item>

      {!isEdit && (
        <Form.Item
          name="code"
          label="Mã nhà cung cấp"
          rules={[{ required: true, message: 'Vui lòng nhập mã nhà cung cấp!' }]}
        >
          <Input placeholder="Nhập mã nhà cung cấp" />
        </Form.Item>
      )}

      <Form.Item name="address" label="Địa chỉ">
        <Input.TextArea rows={3} placeholder="Nhập địa chỉ" />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Số điện thoại"
        rules={[
          {
            pattern: /^[0-9+\-\s()]*$/,
            message: 'Số điện thoại không hợp lệ!',
          },
        ]}
      >
        <Input placeholder="Nhập số điện thoại" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          {
            type: 'email',
            message: 'Email không hợp lệ!',
          },
        ]}
      >
        <Input placeholder="Nhập email" />
      </Form.Item>

      <Form.Item name="contact_person" label="Người liên hệ">
        <Input placeholder="Nhập tên người liên hệ" />
      </Form.Item>

      {!hideActions && (
        <Form.Item>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel} icon={<CloseOutlined />}>
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
            >
              {isEdit ? 'Cập nhật' : 'Thêm'}
            </Button>
          </div>
        </Form.Item>
      )}
    </Form>
  );

  if (noCard) {
    return formContent;
  }

  return (
    <Card title={isEdit ? 'Cập nhật nhà cung cấp' : 'Thêm nhà cung cấp mới'} className="form-container">
      {formContent}
    </Card>
  );
};

export default SupplierForm; 